import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Grid,
    Avatar,
    Stack,
} from "@mui/material";
import { fetchDrawList } from "../../../api/ApiService";
import theme from "../../../theme/Theme";
import { ticketDateFormat, formatNumber } from "../../../utils/common";

import { styled } from "@mui/material/styles";
import {
    AccountBalanceWallet,
    CalendarMonth,
    Timeline,
    EmojiEvents,
    Percent,
    People,
    CheckCircle,
    Payment,
    ConfirmationNumberOutlined,
    DriveFileRenameOutline,
} from "@mui/icons-material";

import { blockchainNetworks } from "../../../config/blockchain";
import { NoRecordFound } from "../../../assets";
import DashboardPageTitle from "../../../component/DashboardPageTitle";
import CustomButton from "../../../component/CustomButton";
import DashboardLoader from "../../../component/DashboardLoader";




// Custom styled avatar for icons
const IconAvatar = styled(Avatar)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.custom.white,
    width: "48px",
    height: "48px",
    marginRight: theme.spacing(2),
}));

const AdminDrawDetails = () => {
    const { drawId } = useParams();
    const [draw, setDraw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [blockchainData, setBlockchainData] = useState("");
    const [rewardData, setRewardData] = useState([]);


    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchDrawList();
                if (response && response.data) {
                    const data = response.data.find((d) => d.drawId === drawId);
                    setDraw(data);
                    if (data) {
                        const blockchainInfo = blockchainNetworks[data.blockchain];
                        setBlockchainData(blockchainInfo || "N/A");
                        setRewardData(data.winnerLevels);
                    }
                }
            } catch (error) {
                console.error("Error fetching draw details:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [drawId]);

    if (loading) {
        return (
            <DashboardLoader
                isLoading={loading} />
        );
    }

    if (!draw) {
        return (
            <Stack direction={"column"} alignItems={"center"} gap={1}>
                <Box
                    component={"img"}
                    src={NoRecordFound}
                    sx={{ width: "300px", height: "300px" }}
                />
                <Typography sx={styles.noDataContainer}>
                    No Draws Found
                </Typography>
            </Stack>
        );
    }

    const getStatus = (status, endTime) => {
        const isExpired = new Date(endTime) < new Date();
        switch (status) {
          case 1:
            return isExpired ? "Expired" : "Yet to Start";
          case 2:
            return isExpired ? "Expired" : "Started";
          case 3:
            return "Lottery Ended";
          case 4:
            return "Winners Count Defined";
          case 5:
            return "Winners Defined";
          case 6:
            return "Awards Distributed";
          default:
            return "Unknown Status";
        }
      };
      

    const getPrizeAmount = (percentage) => {
        if (percentage) {
            const prize = (draw.maxParticipants * percentage * draw.ticketPrice) / 100;
            return prize;
        }
        return 0;
    };
    const sortTopWinnerData = rewardData?.sort((a, b) => parseInt(a.level, 10) - parseInt(b.level, 10));
    return (
        <Box>
            <DashboardPageTitle title={"Draw Details"} showIcon={false} />
            <Box sx={styles.container}>
                <Grid container spacing={4}>

                    <Grid item xs={12} md={6}>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <DriveFileRenameOutline/>
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Draw Name
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {draw.drawName}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <Payment/>
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Draw ID
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {draw.drawId}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>


                    <Grid item xs={12} md={6}>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <ConfirmationNumberOutlined/>
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Ticket Price
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {formatNumber(draw.ticketPrice)} {draw.currencyTicker}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <AccountBalanceWallet />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Currency
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {draw.currencyTicker}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Participants and Status */}
                    <Grid item xs={12} md={6}>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <People />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Participants
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {draw.participantCount}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <CheckCircle />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Status
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {getStatus(draw.status, draw.endTime)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* End Time and Network */}
                    <Grid item xs={12} md={6}>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <CalendarMonth />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    End Time
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {ticketDateFormat(draw.endTime)} (GMT 00:00)
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <Timeline />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Network
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {blockchainData?.networkName}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Prize Pool and Reward Pool Prize */}
                    <Grid item xs={12} md={6}>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <EmojiEvents />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Prize Pool
                                </Typography>
                                {sortTopWinnerData?.slice(0, 3).map((item, index) => (
                                    <>
                                        <Typography key={`prize-${index}`} sx={{ ...styles.detailValue, }}>
                                            {`${["1st Prize", "2nd Prize", "3rd Prize"][index]} -  ${formatNumber(getPrizeAmount(item.percentage))} ${draw.currencyTicker}`}
                                        </Typography>

                                    </>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <AccountBalanceWallet />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Reward Pool Prize
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {formatNumber(draw.rewardPoolPrize)} {draw.currencyTicker}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Win Probability and Total Winners */}
                    <Grid item xs={12} md={6}>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <Percent />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Win Probability
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {draw.winningProbability}%
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={styles.detailItem}>
                            <IconAvatar>
                                <People />
                            </IconAvatar>
                            <Box>
                                <Typography variant="subtitle1" sx={styles.detailLabel}>
                                    Total Winners
                                </Typography>
                                <Typography variant="body1" sx={styles.detailValue}>
                                    {draw.totalWinners}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>


            {/* Back Button */}
            <Box sx={styles.backButtonContainer}>
                <CustomButton
                    title={"Back to Draws List"}

                    onClick={() => window.history.back()}
                />
            </Box>
        </Box>
    );
};

export default AdminDrawDetails;

// Styles
const styles = {
    container: {
        borderRadius: "16px",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.2)",
        padding: "40px",
        marginTop: "20px",
    },

    detailItem: {
        display: "flex",
        alignItems: "center",
        marginBottom: "20px",
    },
    detailLabel: {
        color: theme.palette.text.secondary,
        fontWeight: 500,
        fontSize: "1.2rem",
    },
    detailValue: {
        color: theme.palette.text.primary,
        fontWeight: 500, // Adjusted font weight
        fontSize: "1.1rem", // Decreased font size
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
    },
    noDataContainer: {
        color: "#525252",
        fontSize: "1rem",
        fontWeight: 500,
        textAlign: "center",
        padding: "1em 0",
    },
    backButtonContainer: {
        display: "flex",
        justifyContent: "center",
        marginTop: "20px",
    },

};